import React, { useEffect, useMemo, useState } from 'react';
import LeafletMap from '../../components/LeafletMap';
import { Button, Input } from '../../components/StyledComponents';
import { RoundedButton } from '../../components/ui/RoundedButton';
import Store from '../../store/Store';
import { IconClose, IconRight } from '../../components/Icons';
import DronePage from './DronePage'; 
import { Section } from '../../components/ui/Section';
import { useDialog } from '../../hooks/useDialog';

const polygonRadius = 250;

interface GeoFeature {
	id?: string;
	_selected?: boolean;
	properties: { areaBasePrice?: number; decisionFee?: number; applicationFee?: number };
}

interface GeoMesh {
	uuid: string;
	name: string;
	serialNumber?: string;
	callsign?: string;
	deviceOnlineStatus?: boolean;
	modeCode?: number;
	stationModel?: StationModelInfo;
	cameras?: StationCameraInfo[];
	_geo?: unknown;
	droneId?: number;
}

interface GeoJSON {
	_uuid?: string;
	_isAllStations?: boolean;
	_isViewport?: boolean;
	_center?: { lat: number; lng: number };
	features?: Array<{ geometry?: { coordinates?: number[][][] } }>;
}

interface DroneTypeInfo {
	id: number;
	name: string;
	reachDiameter: number;
	flightSpeed: number;
	startStopTime: number;
	maxFlightTime: number;
	fhModelKey?: string;
	fhDomain?: string;
	fhType?: string;
	fhSubType?: string;
	fhDeviceClass?: string;
}

interface Drone {
	id: number;
	name: string;
	serialNumber?: string;
	callsign?: string;
	deviceOnlineStatus?: boolean;
	modeCode?: number;
	droneType: DroneTypeInfo;
}

interface GatewayModelInfo {
	key: string;
	domain: string;
	type: string;
	subType: string;
	name: string;
	deviceClass: string;
}

interface GatewayCameraInfo {
	cameraIndex: string;
	cameraPosition: string;
}

interface GatewayInfo {
	sn: string;
	callsign: string;
	online: boolean;
	modeCode?: number;
	deviceModel?: GatewayModelInfo;
	cameraList?: GatewayCameraInfo[];
}

interface StationModelInfo {
	id: number;
	key: string;
	domain: string;
	type: string;
	subType: string;
	name: string;
	deviceClass: string;
}

interface StationCameraInfo {
	id: number;
	cameraIndex: string;
	cameraPosition: string;
}

interface GeoSetup {
	map?: { getBounds: () => { getNorthWest: () => { lat: number; lng: number }; getSouthEast: () => { lat: number; lng: number } } };
	onSaveGeo?: (values: (number | string)[]) => void;
}

export interface MapEditPageProps {
	id?: string;
}

function getCenter(geo: GeoJSON): { lat: number; lng: number } | null {
	if (!geo?.features?.length) return null;
	const coos = geo.features[0].geometry?.coordinates?.[0];
	if (!coos?.length) return null;
	return {
		lat: (coos[0][1] + coos[3][1]) / 2,
		lng: (coos[0][0] + coos[3][0]) / 2,
	};
}

export default function MapEditPage({ id }: MapEditPageProps) {
	const { showConfirmDialog } = useDialog();
	const [
		features,
		geoJSON,
		geoMeshes,
		geoMode,
		geoCenter,
		snappedCenter,
		drones,
		selectedDrone,
		availableGateways,
	] = Store.use(
		'features',
		'geoJSON',
		'geoMeshes',
		'geoMode',
		'geoCenter',
		'snappedCenter',
		'drones',
		'selectedDrone',
		'availableGateways'
	) as [
		Record<string, GeoFeature>,
		GeoJSON | null,
		GeoMesh[] | undefined,
		string,
		{ lat: number; lng: number } | null,
		{ lat: number; lng: number } | null,
		Drone[] | undefined,
		{ id: number } | null,
		GatewayInfo[] | undefined,
	];

	const [values, setValues] = useState<(number | string)[]>([100, 0, 0]);
	const [createValues, setCreateValues] = useState<number[]>([100, 0, 0]);
	const [name, setName] = useState('');
	const [selectedGatewaySn, setSelectedGatewaySn] = useState('');
	const [showDrones, setShowDrones] = useState(false);
	const [moveLat, setMoveLat] = useState<string>('');
	const [moveLng, setMoveLng] = useState<string>('');

	const geoSetup = useMemo<GeoSetup>(() => {
		Store.loadStations();
		Store.loadAllStationsCells();
		Store.loadDrones();
		Store.loadAvailableGateways();
		return {};
	}, []);

	useEffect(() => {
		let val: (number | string)[] = [100, 0, 0];
		Object.values(features ?? {}).forEach((feature, i) => {
			const v = [
				feature.properties?.areaBasePrice ?? '',
				feature.properties?.decisionFee ?? '',
				feature.properties?.applicationFee ?? '',
			];
			if (i === 0) {
				val = v;
			} else {
				if (val[0] !== '' && v[0] !== val[0]) val[0] = '';
				if (val[1] !== '' && v[1] !== val[1]) val[1] = '';
				if (val[2] !== '' && v[2] !== val[2]) val[2] = '';
			}
		});
		setValues(val);
	}, [features]);

	const onSelectMap = (station: GeoMesh) => {
		Object.values(features ?? {}).forEach((f) => (f._selected = false));
		setValues([100, 0, 0]);
		setName('');
		Store.set({ features: {}, geoCenter: null });

		if (!station._geo) {
			Store.fetch('provider/station/' + station.uuid).then((result: { geoJson?: string; centerLatitude?: number; centerLongitude?: number }) => {
				if (result?.geoJson) {
					const geo = JSON.parse(result.geoJson) as GeoJSON;
					if (result.centerLatitude != null && result.centerLongitude != null) {
						geo._center = { lat: result.centerLatitude, lng: result.centerLongitude };
						setMoveLat(String(result.centerLatitude));
						setMoveLng(String(result.centerLongitude));
					} else {
						geo._center = getCenter(geo) ?? undefined;
						if (geo._center) {
							setMoveLat(String(geo._center.lat));
							setMoveLng(String(geo._center.lng));
						}
					}
					(geo as GeoJSON & { _name?: string; _uuid?: string; _isAllStations?: boolean; _isViewport?: boolean })._name = station.name;
					(geo as GeoJSON & { _uuid?: string })._uuid = station.uuid;
					(geo as GeoJSON & { _isAllStations?: boolean })._isAllStations = false;
					(geo as GeoJSON & { _isViewport?: boolean })._isViewport = false;
					(station as GeoMesh & { _geo?: GeoJSON })._geo = geo;
					Store.set('geoJSON', geo);
				}
			});
		} else {
			const geo = station._geo as GeoJSON | undefined;
			if (geo) {
				Store.set('geoJSON', geo);
				if (geo._center) {
					setMoveLat(String(geo._center.lat));
					setMoveLng(String(geo._center.lng));
				}
			}
		}
	};

	const onShowAllStations = () => {
		Object.values(features ?? {}).forEach((f) => (f._selected = false));
		setValues(['', '', '']);
		setName('');
		Store.set({ features: {}, geoCenter: null });
		Store.loadAllStationsCells();
	};

	const onShowViewport = () => {
		Object.values(features ?? {}).forEach((f) => (f._selected = false));
		setValues(['', '', '']);
		setName('');
		Store.set({ features: {}, geoCenter: null });
		const map = (geoSetup as GeoSetup & { map?: { getBounds: () => { getNorthWest: () => { lat: number; lng: number }; getSouthEast: () => { lat: number; lng: number } } } }).map;
		if (!map) return;
		const bounds = map.getBounds();
		const topLeft = bounds.getNorthWest();
		const bottomRight = bounds.getSouthEast();
		Store.loadViewportStationsCells(
			{ lat: topLeft.lat, lng: topLeft.lng },
			{ lat: bottomRight.lat, lng: bottomRight.lng }
		);
	};

	const switchMode = (mode: string) => {
		if (mode === 'create') {
			Store.set({ geoMode: mode as 'create' | 'edit', geoJSON: null, selectedDrone: null });
			Store.loadAvailableGateways();
			setCreateValues([100, 0, 0]);
			setSelectedGatewaySn('');
		} else {
			Store.set({ geoMode: mode as 'create' | 'edit', geoJSON: null });
		}
	};

	const onSaveGeo = () => {
		const centerToUse = snappedCenter || geoCenter;
		Store.createStation(
			name,
			selectedGatewaySn,
			centerToUse,
			selectedDrone?.id,
			geoCenter,
			{ areaBasePrice: createValues[0], decisionFee: createValues[1], applicationFee: createValues[2] }
		)
			.then(() => {
				setName('');
				setSelectedGatewaySn('');
			})
			.catch((err: unknown) => console.error('Error creating mesh features:', err));
	};

	const onDeleteStation = (station: GeoMesh) => {
		showConfirmDialog({
			title: 'Fläche löschen',
			text: 'Sind Sie sicher, dass Sie die Fläche für "' + station.name + '" löschen möchten?',
			confirmLabel: 'Ja',
			cancelLabel: 'Nein',
			confirmFirst: true,
			onConfirm: () => {
				Store.deleteStation(station.uuid).then(() => {});
			},
		});
	};

	return (
		<div className="order-page">
			<div className="order-page-left">
				<Section>
					<div className="rounded-button-bar">
						<RoundedButton
							data-pw="button-edit-area"
							selected={!showDrones && geoMode === 'edit'}
							onClick={() => { setShowDrones(false); switchMode('edit'); }}
						>
							Drohnenstationen bearbeiten
						</RoundedButton>
						<RoundedButton
							data-pw="button-create-area"
							selected={!showDrones && geoMode === 'create'}
							onClick={() => { setShowDrones(false); switchMode('create'); }}
						>
							Drohnenstationen erstellen
						</RoundedButton>
						<RoundedButton
							data-pw="button-drones"
							selected={showDrones}
							onClick={() => setShowDrones(true)}
						>
							Drohnen verwalten
						</RoundedButton>
					</div>
				</Section>

				{showDrones && <DronePage />}

				{!showDrones && geoMode === 'edit' && (
					<>
						<Section>
							<h2>Drohnenstationen</h2>
							<div className="flex flex-col gap-0">
								<button
									data-pw="button-show-all-areas"
									className={'mesh-button' + (geoJSON?._isAllStations ? ' selected' : '')}
									onClick={onShowAllStations}
								>
									<img src="./assets/map_icon.png" alt="Mesh Icon" className="w-7 h-7 opacity-80 p-1" />
									<span className="mr-auto font-semibold">Alle Drohnenstationen anzeigen</span>
									<IconRight />
								</button>
								<button
									data-pw="button-show-viewport-areas"
									className={'mesh-button' + (geoJSON?._isViewport ? ' selected' : '')}
									onClick={onShowViewport}
								>
									<img src="./assets/map_icon.png" alt="Mesh Icon" className="w-7 h-7 opacity-80 p-1" />
									<span className="mr-auto font-semibold">Viewport</span>
									<IconRight />
								</button>
								{geoMeshes?.length ? (
									geoMeshes.map((mesh) => (
										<button
											data-pw="button-select-area"
											key={mesh.uuid}
											className={
												'mesh-button' +
												(geoJSON && !geoJSON._isAllStations && geoJSON._uuid === mesh.uuid ? ' selected' : '')
											}
											onClick={() => onSelectMap(mesh)}
										>
											<img src="./assets/map_icon.png" alt="Mesh Icon" className="w-7 h-7 opacity-80 p-1" />
											<span className="mr-auto">{mesh.name}</span>
											<div
												onClick={(e) => {
													e.stopPropagation();
													e.preventDefault();
													onDeleteStation(mesh);
												}}
												role="button"
											>
												<IconClose />
											</div>
											<IconRight />
										</button>
									))
								) : (
									<div>Keine Flächen vorhanden.</div>
								)}
							</div>
						</Section>

						{geoJSON && (
							<Section>
								<h2>Drohnenstationen verwalten</h2>
								<>
									<div>
										Drohnenstation ID:{' '}
										{Object.values(features ?? {})
											.map((f) => f.id)
											.join(', ')}
									</div>
									{!geoJSON._isAllStations && !geoJSON._isViewport && (() => {
										const station = geoMeshes?.find((m) => m.uuid === geoJSON._uuid);
										return station ? (
											<div className="mt-4 mb-2 rounded-lg border border-gray-200 bg-gray-50 p-3 flex flex-col gap-2 text-sm">
												<div className="flex items-center justify-between gap-2">
													<span className="font-semibold text-gray-800">{station.name}</span>
													{station.deviceOnlineStatus !== undefined && (
														<span className={`text-xs px-2 py-0.5 rounded-full font-medium ${station.deviceOnlineStatus ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
															{station.deviceOnlineStatus ? 'Online' : 'Offline'}
														</span>
													)}
												</div>
												{station.callsign && (
													<div className="flex gap-2">
														<span className="text-gray-500 w-28 shrink-0">Callsign</span>
														<span className="text-gray-800">{station.callsign}</span>
													</div>
												)}
												{station.serialNumber && (
													<div className="flex gap-2">
														<span className="text-gray-500 w-28 shrink-0">Serial</span>
														<span className="text-gray-800 font-mono text-xs">{station.serialNumber}</span>
													</div>
												)}
												{station.modeCode !== undefined && station.modeCode !== null && (
													<div className="flex gap-2">
														<span className="text-gray-500 w-28 shrink-0">Mode</span>
														<span className="text-gray-800">{station.modeCode}</span>
													</div>
												)}
												{station.stationModel && (
													<div className="flex flex-col gap-1 pt-1 border-t border-gray-200">
														<span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Gerätemodell</span>
														<div className="flex gap-2">
															<span className="text-gray-500 w-28 shrink-0">Name</span>
															<span className="text-gray-800">{station.stationModel.name}</span>
														</div>
														{station.stationModel.key && (
															<div className="flex gap-2">
																<span className="text-gray-500 w-28 shrink-0">Key</span>
																<span className="text-gray-800 font-mono text-xs">{station.stationModel.key}</span>
															</div>
														)}
														<div className="flex gap-2">
															<span className="text-gray-500 w-28 shrink-0">Klasse</span>
															<span className="text-gray-800">{station.stationModel.deviceClass}</span>
														</div>
														<div className="flex gap-4 text-xs text-gray-500 mt-0.5">
															<span>Domain: {station.stationModel.domain}</span>
															<span>Type: {station.stationModel.type}</span>
															<span>Sub: {station.stationModel.subType}</span>
														</div>
													</div>
												)}
												{station.cameras && station.cameras.length > 0 && (
													<div className="flex flex-col gap-1 pt-1 border-t border-gray-200">
														<span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Kameras</span>
														{station.cameras.map((cam) => (
															<div key={cam.id} className="flex gap-2">
																<span className="text-gray-500 w-28 shrink-0">{cam.cameraPosition}</span>
																<span className="text-gray-800 font-mono text-xs">{cam.cameraIndex}</span>
															</div>
														))}
													</div>
												)}
											</div>
										) : null;
									})()}
								{!geoJSON._isAllStations && !geoJSON._isViewport && (() => {
									const mesh = geoMeshes?.find((m) => m.uuid === geoJSON._uuid);
									const assignedDrone = drones?.find((d) => d.id === mesh?.droneId);
									return (
										<div className="flex flex-col gap-1 mt-2 pb-4">
											<label className="text-sm font-medium text-gray-700">Drohne für diese Drohnenstation</label>
											{assignedDrone ? (
												<div className="p-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-800">
													<span className="font-semibold">{assignedDrone.name}</span>
													{assignedDrone.droneType && (
														<span className="text-gray-500 ml-2">
															— {assignedDrone.droneType.name} ({assignedDrone.droneType.reachDiameter}m)
														</span>
													)}
													{assignedDrone.serialNumber && (
														<span className="block text-xs text-gray-400 mt-0.5 font-mono">{assignedDrone.serialNumber}</span>
													)}
												</div>
											) : (
												<p className="text-xs text-gray-400 italic">
													Noch keine Drohne zugewiesen. Drohne im "Drohnen"-Tab registrieren.
												</p>
											)}
										</div>
									);
								})()}
								{!geoJSON._isAllStations && !geoJSON._isViewport && (
									<div className="flex flex-col gap-2 mt-2 pb-4 border-t border-gray-200 pt-3">
										<label className="text-sm font-medium text-gray-700">Station verschieben</label>
										<div className="flex gap-4">
											<Input
												label="Latitude"
												type="number"
												value={moveLat}
												onChange={(value) => setMoveLat(String(value))}
											/>
											<Input
												label="Longitude"
												type="number"
												value={moveLng}
												onChange={(value) => setMoveLng(String(value))}
											/>
										</div>
										<div className="flex justify-end">
											<Button
												enabled={!!geoJSON._uuid && moveLat !== '' && moveLng !== ''}
												onClick={() => {
													if (!geoJSON._uuid) return;
													const latNum = parseFloat(moveLat);
													const lngNum = parseFloat(moveLng);
													if (isNaN(latNum) || isNaN(lngNum)) return;
													Store.moveStation(geoJSON._uuid, latNum, lngNum);
												}}
											>
												Station-Position speichern
											</Button>
										</div>
									</div>
								)}
								<div className="flex gap-4 pb-8">
										<Input
											data-pw="input-area-base-price"
											label="Basispreis"
											type="number"
											value={String(values[0] ?? '')}
											onChange={(value) => setValues([parseInt(String(value), 10) || 0, values[1], values[2]])}
										/>
										<Input
											data-pw="input-area-decision-fee"
											label="Bescheid beantragen Kosten"
											type="number"
											value={String(values[1] ?? '')}
											onChange={(value) => setValues([values[0], parseInt(String(value), 10) || 0, values[2]])}
										/>
										<Input
											data-pw="input-area-application-fee"
											label="Bescheid Kosten"
											type="number"
											value={String(values[2] ?? '')}
											onChange={(value) => setValues([values[0], values[1], parseInt(String(value), 10) || 0])}
										/>
									</div>
									<Button
										data-pw="button-save-area"
										enabled={Object.values(features ?? {}).length > 0}
										onClick={() => geoSetup.onSaveGeo?.(values)}
									>
										Änderungen speichern
									</Button>
								</>
							</Section>
						)}
					</>
				)}

			{!showDrones && geoMode === 'create' && (
				<Section>
					<h2>Neue Drohnenstation konfigurieren</h2>
					<Input
						data-pw="input-new-area-name"
						type="text"
						placeholder="Name"
						onChange={(value) => setName(value)}
						value={name}
					/>
					<div className="flex flex-col gap-2 mt-2 mb-2">
						<label className="text-sm font-medium text-gray-700">
							Drohnenstation (Gateway) <span className="text-red-500">*</span>
						</label>
						{availableGateways && availableGateways.length > 0 ? (
							<select
								data-pw="select-gateway-sn"
								className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
								value={selectedGatewaySn}
								onChange={(e) => setSelectedGatewaySn(e.target.value)}
							>
								<option value="">Gateway auswählen...</option>
								{availableGateways.map((gw) => (
									<option key={gw.sn} value={gw.sn}>
										{gw.callsign} ({gw.sn}){gw.online ? ' ✓' : ''}
									</option>
								))}
							</select>
						) : (
							<div className="text-sm text-gray-500 p-2 bg-gray-50 rounded border border-gray-200">
								Keine verfügbaren Gateways gefunden.
							</div>
						)}
					</div>
					<div>
							Wähle eine Position auf der Karte aus, um eine Drohnenstation zu konfigurieren.
							<div className="flex gap-4 py-2">
								<Input
									data-pw="input-new-area-latitude"
									label="Latitude"
									type="number"
									value={geoCenter?.lat != null ? String(geoCenter.lat) : ''}
									onChange={(value) => {
										const lat = parseFloat(value);
										if (isNaN(lat)) return;
										const newCenter = { lat, lng: geoCenter?.lng ?? 15.0 };
										Store.set('geoCenter', newCenter);
										Store.snapToGrid(lat, newCenter.lng);
									}}
									step={0.0001}
								/>
								<Input
									data-pw="input-new-area-longitude"
									label="Longitude"
									type="number"
									value={geoCenter?.lng != null ? String(geoCenter.lng) : ''}
									onChange={(value) => {
										const lng = parseFloat(value);
										if (isNaN(lng)) return;
										const newCenter = { lat: geoCenter?.lat ?? 48.0, lng };
										Store.set('geoCenter', newCenter);
										Store.snapToGrid(newCenter.lat, lng);
									}}
									step={0.0001}
								/>
							</div>
							<div className="flex gap-4 py-4">
								<Input
									data-pw="input-new-area-base-price"
									label="Basispreis"
									type="number"
									value={String(createValues[0])}
									onChange={(value) =>
										setCreateValues([parseInt(String(value), 10) || 0, createValues[1], createValues[2]])
									}
								/>
								<Input
									data-pw="input-new-area-decision-fee"
									label="Bescheid beantragen Kosten"
									type="number"
									value={String(createValues[1])}
									onChange={(value) =>
										setCreateValues([createValues[0], parseInt(String(value), 10) || 0, createValues[2]])
									}
								/>
								<Input
									data-pw="input-new-area-application-fee"
									label="Bescheid Kosten"
									type="number"
									value={String(createValues[2])}
									onChange={(value) =>
										setCreateValues([createValues[0], createValues[1], parseInt(String(value), 10) || 0])
									}
								/>
							</div>
							{geoCenter && snappedCenter && (
								<div className="flex flex-col gap-2 mt-4 pb-8">
									Aligned Position:{' '}
									<span className="font-bold">Lat: {snappedCenter.lat?.toFixed(6)}</span>
									<span className="font-bold">Lng: {snappedCenter.lng?.toFixed(6)}</span>
								</div>
							)}
							<div className="flex flex-col gap-2 mt-4 pb-8">
								Polygon Radius (m): <span className="font-bold">{polygonRadius}</span>
							</div>
						<div className="text-xs text-gray-500 italic pb-4">
							Die Drohnenzuweisung erfolgt automatisch anhand der FlightHub-Paarung.
						</div>
						</div>
					<Button
						data-pw="button-create-area"
						enabled={name.length > 0 && geoCenter !== null && selectedGatewaySn.length > 0}
						onClick={onSaveGeo}
					>
						Drohnenstation verwalten
					</Button>
					</Section>
				)}
			</div>

			<div className="order-page-right">
				<LeafletMap
					geoSearch={geoMode === 'create'}
					editGeo={geoSetup as unknown as Record<string, unknown>}
				/>
			</div>
		</div>
	);
}
